import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckboxWrapper,
  Label,
  Checkbox,
  HiddenInput,
  Hint,
  Count,
} from './CheckboxSelector.styles';
import CheckMarkIcon from '../../assets/icons/CheckMarkIcon';

interface CheckboxTypes {
  value: string;
  checked: boolean;
  filterType: string;
  onChange: (option: string, newChecked: boolean) => void;
  inputName: string;
  disabled?: boolean;
  count?: number;
  popoverTitle?: string;
  popoverDescription?: string;
  isDefaultDealer?: boolean;
  isPartnerDealer?: boolean;
  text?: string;
}

const CheckboxSelector: React.FC<CheckboxTypes> = ({
  value,
  filterType,
  checked,
  onChange,
  inputName,
  count,
  disabled = false,
  isPartnerDealer,
  text,
}) => {
  const { t } = useTranslation();
  const handleChange = useCallback(() => {
    if (disabled) return;
    onChange(value, !checked);
  }, [disabled, onChange, value, checked]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key !== ' ') return;
      e.preventDefault();
      handleChange();
    },
    [disabled, handleChange],
  );

  const label = useMemo(() => {
    if (filterType === 'modelYear') return value;
    if (filterType === 'wheelSize') return `${value}"`;
    if (
      filterType === 'otherMakes' ||
      filterType === 'nonAudiModels' ||
      filterType === 'partnerDealers'
    )
      return `${text}`;
    return t(`vlp.filters.filterOptions.${filterType}.${value}`);
  }, [filterType, t, text, value]);

  const getAriaLabel = () => {
    if (filterType === 'wheelSize') return t(value.toString());
    return t(`vlp.filters.filterOptions.${filterType}.${value}`);
  };

  const ariaLabel = getAriaLabel();

  const isHintAvailable = filterType === 'trimLine' || filterType === 'drivetrain';
  const hint = t(`vlp.filters.filterOptions.${filterType}.hints.${value}`);

  const shouldFilterHasCounts = filterType === 'modelYear';

  return (
    <CheckboxWrapper disabled={disabled} isPartnerDealer={isPartnerDealer}>
      <Checkbox
        tabIndex={disabled ? -1 : 0}
        onClick={handleChange}
        role="checkbox"
        aria-label={ariaLabel}
        aria-checked={checked}
        onKeyDown={handleKeyPress}
        checked={checked}
        disabled={disabled}
        isPartnerDealer={isPartnerDealer}
      >
        {checked && <CheckMarkIcon />}
      </Checkbox>
      <HiddenInput
        id={inputName}
        type="checkbox"
        onChange={handleChange}
        checked={checked}
        name={value}
      />
      <Label
        htmlFor={inputName}
        disabled={disabled}
        isPartnerDealer={isPartnerDealer}
        data-testid={`checkbox-${value}`}
        isFlex={shouldFilterHasCounts}
      >
        {label}
        {!!count && <Count data-testid="checkbox-count">({count})</Count>}
        {isHintAvailable && <Hint data-testid={`hint-${value}`}> ({hint}) </Hint>}
      </Label>
    </CheckboxWrapper>
  );
};

export default CheckboxSelector;
